import { animated } from "@react-spring/web";
import { keyBy } from "lodash";
import { useTranslation } from "react-i18next";

import Icon from "Atoms/Icons";

import {
  ResponsiveTitleBar,
  ResponsivePrimaryAction,
  LinkButton,
  Price,
} from "../Atoms";
import { AccordionDropdown } from "../BillSplit/Atoms";
import { useDeleteOrderItemClaim } from "../BillSplit/OrderItemClaims/queries";
import {
  useCreateOrderItemClaim,
  MissingCustomer,
  UnclaimedOrderItem,
  OrderItemBox,
  ClaimableOrderItemContent,
} from "../BillSplit/SelectOrderItems";
import { useLoadedBillSplit } from "../BillSplit/queries";
import { BillSplit, OrderItemClaim } from "../BillSplit/schema";
import {
  getClaimedOrderItemsIn,
  getPaidAtTillAmount,
  getBillSplitPaidOrderItems,
  getUnclaimedOrderItems,
} from "../BillSplit/selectors";
import { useOrderItemsTransitions } from "../BillSplit/useOrderItemsTransitions";
import { bustleBeeForCustomer } from "../Customer/bustleBees";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { useLoadedCustomerTab } from "./queries";
import { OrderItem } from "./schema";
import { getOrderItemTotals } from "./selectors";

export const BillReview = ({}: {}) => {
  const { t } = useTranslation();
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplit } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const createCreateOrderItemClaim = useCreateOrderItemClaim(billSplit);
  const claimItem = (orderItemUuid: string) => async () => {
    createCreateOrderItemClaim.mutate(orderItemUuid);
  };

  const orderItems = customerTab.orders.flatMap((o) => o.orderItems);
  const orderItemClaims = keyBy(billSplit.orderItemClaims, "orderItemId");
  const customers = keyBy(billSplit.customers, "uuid");
  const unclaimedOrderItems = getUnclaimedOrderItems(billSplit, customerTab);
  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab
  );

  const yourOrderItems = getClaimedOrderItemsFor(customer);
  const claimWithCustomer = (claim: OrderItemClaim) => {
    return {
      ...claim,
      customer: customers[claim.customerId] || MissingCustomer,
    };
  };
  const completedSessionIds = billSplit.customerSessions
    .filter((cs) => !!cs.completedAt)
    .map((cs) => cs.uuid);

  const paidOrderItems = getBillSplitPaidOrderItems(billSplit, customerTab);

  const unpaidClaimedOrderItems = orderItems.filter(
    (oi) =>
      !!orderItemClaims[oi.uuid] &&
      !completedSessionIds.includes(orderItemClaims[oi.uuid]?.customerSessionId)
  );

  const unclaimedOrderItemsTransitions =
    useOrderItemsTransitions(unclaimedOrderItems);
  const paidOrderItemsTransitions = useOrderItemsTransitions(paidOrderItems);
  const unpaidClaimedOrderItemTransitions = useOrderItemsTransitions(
    unpaidClaimedOrderItems
  );

  const unclaimedOrderItemsTotal = getOrderItemTotals(unclaimedOrderItems);
  const unpaidClaimedOrderItemsTotal = getOrderItemTotals(
    unpaidClaimedOrderItems
  );
  const paidOrderItemsTotal = getOrderItemTotals(paidOrderItems);
  const paidAtTillAmount = getPaidAtTillAmount(billSplit, customerTab);

  return (
    <div className="space-y-32 max-w-screen-sm mx-auto">
      <ResponsiveTitleBar>{t("billSplit.yourBill")}</ResponsiveTitleBar>
      <AccordionDropdown
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.unselected", { count: unclaimedOrderItems.length })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={unclaimedOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8">
          {unclaimedOrderItemsTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <UnclaimedOrderItem
                orderItem={oi}
                claimItem={claimItem(oi.uuid)}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <AccordionDropdown
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.selected", {
                count: unpaidClaimedOrderItems.length,
              })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={unpaidClaimedOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8 overflow-hidden">
          {unpaidClaimedOrderItemTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <TheirClaimedOrderItem
                orderItem={oi}
                claim={claimWithCustomer(orderItemClaims[oi.uuid])}
                billSplit={billSplit}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <AccordionDropdown
        isDefaultClosed
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.paid", { count: paidOrderItems.length })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={paidOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8">
          {paidOrderItemsTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <TheirClaimedOrderItem
                orderItem={oi}
                billSplit={billSplit}
                claim={claimWithCustomer(orderItemClaims[oi.uuid])}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <h3 className="font-bold text-xl">
        {t("billSplit.paidAtTillAmount")}{" "}
        <Price showCurrency value={paidAtTillAmount} />
      </h3>
      <ResponsivePrimaryAction>
        <LinkButton primary to={`../ownerReview`}>
          {t("common.ok")}
        </LinkButton>
      </ResponsivePrimaryAction>
    </div>
  );
};

const OldUnclaimedOrderItem = ({
  orderItem,
  claimItem,
}: {
  orderItem: OrderItem;
  claimItem: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div
      onClick={claimItem}
      className="hover:border-venue-primary border-venue-tertiary border-2 flex bg-venue-tertiary shadow-venue-brand-style rounded-venue-brand p-12 flex-wrap text-base font-medium items-center gap-8"
    >
      <ClaimableOrderItemContent orderItem={orderItem} />
    </div>
  );
};

export const YourClaimedOrderItem = ({
  orderItem,
  deleteOrderItemClaim,
}: {
  orderItem: OrderItem;
  deleteOrderItemClaim: () => void;
}) => {
  return (
    <div className="hover:border-venue-primary justify-between border-venue-tertiary border-2 flex bg-venue-tertiary shadow-venue-brand-style rounded-venue-brand p-12 flex-wrap text-base font-medium items-center gap-8">
      <ClaimableOrderItemContent orderItem={orderItem} />
      <div>
        <div className="text-venue-highlight">
          <button type="button" onClick={deleteOrderItemClaim}>
            <Icon icon="delete" />
          </button>
        </div>
      </div>
    </div>
  );
};
const TheirClaimedOrderItem = ({
  billSplit,
  orderItem,
  disabled,
  claim,
}: {
  billSplit: BillSplit;
  orderItem: OrderItem;
  disabled?: boolean;
  claim: OrderItemClaim & { customer: Customer };
}) => {
  const customerSession = billSplit.customerSessions.find(
    (cs) => cs.uuid === claim.customerSessionId
  );
  const bustleBee = bustleBeeForCustomer(claim.customer);
  return (
    <OrderItemBox theirClaim disabled>
      <ClaimableOrderItemContent orderItem={orderItem} />
      {bustleBee && (
        <div className="flex gap-8 items-center">
          {claim.customer.nickname}
          <div className="rounded-full bg-white w-32 h-32">
            <img src={bustleBee.img_96} alt={bustleBee.alt} />
          </div>
        </div>
      )}
    </OrderItemBox>
  );
};
